import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  TableSortLabel
} from '@mui/material';
import {
  Add as AddIcon,
  GetApp as ExportIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/apiUtils';

const TicketDashboard = () => {
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Tickets data loaded from API
  const [tickets, setTickets] = useState([]);

  // Form and modal state
  const [openModal, setOpenModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [formData, setFormData] = useState({
    id: '',
    title: '',
    description: '',
    status: 'Backlog',
    priority: 'P2',
    effort: 'M',
    businessValue: 'Medium',
    assignee: '',
    dueDate: '',
    dependencies: ''
  });

  // Filter state
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    assignee: ''
  });

  // Sort state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Load tickets on component mount
  useEffect(() => {
    loadTickets();
  }, []);

  // Load all tickets from API
  const loadTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading tickets from API...');
      
      const response = await apiGet('/api/tickets');
      
      if (response.ok) {
        const ticketsData = await response.json();
        console.log('Tickets loaded successfully:', ticketsData.length);
        setTickets(ticketsData);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load tickets');
      }
    } catch (err) {
      console.error('Error loading tickets:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate score for a ticket
  const calculateScore = (ticket) => {
    const priorityScore = { 'P0': 4, 'P1': 3, 'P2': 2, 'P3': 1 }[ticket.priority] || 1;
    const valueScore = { 'High': 3, 'Medium': 2, 'Low': 1 }[ticket.businessValue] || 1;
    const effortScore = { 'XS': 5, 'S': 4, 'M': 3, 'L': 2, 'XL': 1 }[ticket.effort] || 1;
    return (priorityScore + valueScore) / effortScore;
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      'Backlog': 'default',
      'In Progress': 'info',
      'Review': 'warning',
      'Done': 'success'
    };
    return colors[status] || 'default';
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    const colors = {
      'P0': 'error',
      'P1': 'warning',
      'P2': 'info',
      'P3': 'default'
    };
    return colors[priority] || 'default';
  };

  // Filter tickets
  const filteredTickets = tickets.filter(ticket => {
    if (filters.status && ticket.status !== filters.status) return false;
    if (filters.priority && ticket.priority !== filters.priority) return false;
    if (filters.assignee && ticket.assignee !== filters.assignee) return false;
    return true;
  });

  // Sort tickets
  const sortedTickets = [...filteredTickets].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];
    
    if (sortConfig.key === 'score') {
      aValue = calculateScore(a);
      bValue = calculateScore(b);
    }
    
    if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // Handle sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get unique assignees for filter dropdown
  const uniqueAssignees = [...new Set(tickets.map(t => t.assignee).filter(a => a))];

  // Calculate summary stats
  const summaryStats = {
    total: tickets.length,
    inProgress: tickets.filter(t => t.status === 'In Progress').length,
    highPriority: tickets.filter(t => t.priority === 'P0' || t.priority === 'P1').length,
    overdue: tickets.filter(t => {
      return t.dueDate && new Date(t.dueDate) < new Date() && t.status !== 'Done';
    }).length
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      const ticketData = {
        ...formData,
        dateCreated: editingIndex === -1 ? new Date().toISOString().split('T')[0] : tickets[editingIndex].dateCreated
      };

      if (editingIndex === -1) {
        // Create new ticket
        console.log('Creating new ticket:', ticketData.id);
        const response = await apiPost('/api/tickets', ticketData);
        
        if (response.ok) {
          console.log('Ticket created successfully');
          await loadTickets(); // Reload tickets from server
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create ticket');
        }
      } else {
        // Update existing ticket
        const ticketId = tickets[editingIndex].id;
        console.log('Updating ticket:', ticketId);
        
        // Remove id from ticketData to prevent overriding the URL parameter
        const { id, ...updateData } = ticketData;
        const response = await apiPut(`/api/tickets/${ticketId}`, updateData);
        
        if (response.ok) {
          console.log('Ticket updated successfully');
          await loadTickets(); // Reload tickets from server
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update ticket');
        }
      }

      setOpenModal(false);
      resetForm();
    } catch (err) {
      console.error('Error saving ticket:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      id: '',
      title: '',
      description: '',
      status: 'Backlog',
      priority: 'P2',
      effort: 'M',
      businessValue: 'Medium',
      assignee: '',
      dueDate: '',
      dependencies: ''
    });
    setEditingIndex(-1);
  };

  // Handle edit
  const handleEdit = (index) => {
    const ticket = tickets[index];
    console.log('Editing ticket:', ticket);
    console.log('Ticket ID:', ticket.id);
    
    // Explicitly preserve the ticket ID and ensure all fields are properly mapped
    const editFormData = {
      id: ticket.id,
      title: ticket.title || '',
      description: ticket.description || '',
      status: ticket.status || 'Backlog',
      priority: ticket.priority || 'P2',
      effort: ticket.effort || 'M',
      businessValue: ticket.businessValue || 'Medium',
      assignee: ticket.assignee || '',
      dueDate: ticket.dueDate || '',
      dependencies: ticket.dependencies || ''
    };
    
    console.log('Setting form data:', editFormData);
    setFormData(editFormData);
    setEditingIndex(index);
    setOpenModal(true);
  };

  // Handle delete
  const handleDelete = async (index) => {
    if (window.confirm('Are you sure you want to delete this ticket?')) {
      setLoading(true);
      setError(null);
      
      try {
        const ticketId = tickets[index].id;
        console.log('Deleting ticket:', ticketId);
        
        const response = await apiDelete(`/api/tickets/${ticketId}`);
        
        if (response.ok) {
          console.log('Ticket deleted successfully');
          await loadTickets(); // Reload tickets from server
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete ticket');
        }
      } catch (err) {
        console.error('Error deleting ticket:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['Ticket ID', 'Title', 'Description', 'Status', 'Priority', 'Effort', 'Business Value', 'Score', 'Assignee', 'Due Date', 'Dependencies', 'Date Created'];
    const rows = tickets.map(ticket => [
      ticket.id,
      ticket.title,
      ticket.description,
      ticket.status,
      ticket.priority,
      ticket.effort,
      ticket.businessValue,
      calculateScore(ticket).toFixed(2),
      ticket.assignee || '',
      ticket.dueDate || '',
      ticket.dependencies || '',
      ticket.dateCreated || ''
    ]);

    let csv = headers.join(',') + '\n';
    rows.forEach(row => {
      csv += row.map(cell => `"${cell}"`).join(',') + '\n';
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'development_tickets.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({ status: '', priority: '', assignee: '' });
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Loading and Error States */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <Typography>Loading tickets...</Typography>
        </Box>
      )}
      {error && (
        <Box sx={{ bgcolor: 'error.light', color: 'error.contrastText', p: 2, borderRadius: 1, mb: 2 }}>
          <Typography>Error: {error}</Typography>
        </Box>
      )}
      
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 600 }}>
          Development Tracking Dashboard
        </Typography>
        <Button
          variant="contained"
          startIcon={<ExportIcon />}
          onClick={exportToCSV}
          sx={{ bgcolor: 'success.main', '&:hover': { bgcolor: 'success.dark' } }}
        >
          Export to CSV
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Total Tickets
              </Typography>
              <Typography variant="h3" color="primary.main" fontWeight="bold">
                {summaryStats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                In Progress
              </Typography>
              <Typography variant="h3" color="info.main" fontWeight="bold">
                {summaryStats.inProgress}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                High Priority (P0/P1)
              </Typography>
              <Typography variant="h3" color="warning.main" fontWeight="bold">
                {summaryStats.highPriority}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'background.paper' }}>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Overdue
              </Typography>
              <Typography variant="h3" color="error.main" fontWeight="bold">
                {summaryStats.overdue}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenModal(true)}
            sx={{ bgcolor: 'primary.main' }}
          >
            Add Ticket
          </Button>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="Backlog">Backlog</MenuItem>
              <MenuItem value="In Progress">In Progress</MenuItem>
              <MenuItem value="Review">Review</MenuItem>
              <MenuItem value="Done">Done</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={filters.priority}
              label="Priority"
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
            >
              <MenuItem value="">All Priorities</MenuItem>
              <MenuItem value="P0">P0 - Critical</MenuItem>
              <MenuItem value="P1">P1 - High</MenuItem>
              <MenuItem value="P2">P2 - Medium</MenuItem>
              <MenuItem value="P3">P3 - Low</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Assignee</InputLabel>
            <Select
              value={filters.assignee}
              label="Assignee"
              onChange={(e) => setFilters({ ...filters, assignee: e.target.value })}
            >
              <MenuItem value="">All Assignees</MenuItem>
              {uniqueAssignees.map(assignee => (
                <MenuItem key={assignee} value={assignee}>{assignee}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button variant="outlined" onClick={clearFilters}>
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {/* Tickets Table */}
      <TableContainer component={Paper} sx={{ bgcolor: 'background.paper' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'id'}
                  direction={sortConfig.key === 'id' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('id')}
                >
                  Ticket ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'title'}
                  direction={sortConfig.key === 'title' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell>Description</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'status'}
                  direction={sortConfig.key === 'status' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'priority'}
                  direction={sortConfig.key === 'priority' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('priority')}
                >
                  Priority
                </TableSortLabel>
              </TableCell>
              <TableCell>Effort</TableCell>
              <TableCell>Business Value</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortConfig.key === 'score'}
                  direction={sortConfig.key === 'score' ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort('score')}
                >
                  Score
                </TableSortLabel>
              </TableCell>
              <TableCell>Assignee</TableCell>
              <TableCell>Due Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTickets.map((ticket, index) => {
              const isOverdue = ticket.dueDate && new Date(ticket.dueDate) < new Date() && ticket.status !== 'Done';
              return (
                <TableRow
                  key={ticket.id}
                  sx={{
                    bgcolor: isOverdue ? 'error.main' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <TableCell>{ticket.id}</TableCell>
                  <TableCell>{ticket.title}</TableCell>
                  <TableCell>{ticket.description}</TableCell>
                  <TableCell>
                    <Chip
                      label={ticket.status}
                      color={getStatusColor(ticket.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ticket.priority}
                      color={getPriorityColor(ticket.priority)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{ticket.effort}</TableCell>
                  <TableCell>{ticket.businessValue}</TableCell>
                  <TableCell>{calculateScore(ticket).toFixed(2)}</TableCell>
                  <TableCell>{ticket.assignee || '-'}</TableCell>
                  <TableCell>{ticket.dueDate || '-'}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(tickets.indexOf(ticket))}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(tickets.indexOf(ticket))}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Modal */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingIndex === -1 ? 'Add New Ticket' : 'Edit Ticket'}
          <IconButton
            onClick={() => setOpenModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Ticket ID"
                  value={formData.id}
                  onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                  required
                  placeholder="e.g., FEAT-001"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="Brief description"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={3}
                  placeholder="Detailed explanation"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <MenuItem value="Backlog">Backlog</MenuItem>
                    <MenuItem value="In Progress">In Progress</MenuItem>
                    <MenuItem value="Review">Review</MenuItem>
                    <MenuItem value="Done">Done</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={formData.priority}
                    label="Priority"
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  >
                    <MenuItem value="P0">P0 - Critical</MenuItem>
                    <MenuItem value="P1">P1 - High</MenuItem>
                    <MenuItem value="P2">P2 - Medium</MenuItem>
                    <MenuItem value="P3">P3 - Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Effort Estimate</InputLabel>
                  <Select
                    value={formData.effort}
                    label="Effort Estimate"
                    onChange={(e) => setFormData({ ...formData, effort: e.target.value })}
                  >
                    <MenuItem value="XS">XS (1-2 hours)</MenuItem>
                    <MenuItem value="S">S (Half day)</MenuItem>
                    <MenuItem value="M">M (1-2 days)</MenuItem>
                    <MenuItem value="L">L (3-5 days)</MenuItem>
                    <MenuItem value="XL">XL (1+ week)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Business Value</InputLabel>
                  <Select
                    value={formData.businessValue}
                    label="Business Value"
                    onChange={(e) => setFormData({ ...formData, businessValue: e.target.value })}
                  >
                    <MenuItem value="High">High</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="Low">Low</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Assignee"
                  value={formData.assignee}
                  onChange={(e) => setFormData({ ...formData, assignee: e.target.value })}
                  placeholder="Name"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Due Date"
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Dependencies"
                  value={formData.dependencies}
                  onChange={(e) => setFormData({ ...formData, dependencies: e.target.value })}
                  placeholder="e.g., FEAT-002, FEAT-003"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Save Ticket
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TicketDashboard;
